import React, { useState } from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { Ellipsis, Loader2, PencilLine, Settings, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

type FunnelPagePopoverProps = {
  pageData: {
    pageId: string;
    pageRoute: string;
    name?: string;
  };
  funnelStepId: string;
};

export const FunnelPagePopover = ({
  pageData,
  funnelStepId,
}: FunnelPagePopoverProps) => {
  const projectId = useCurrentProjectId();
  const [isOpen, setIsOpen] = useState(false);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [pageName, setPageName] = useState(pageData.name);
  const [pageRoute, setPageRoute] = useState(pageData.pageRoute);
  const navigate = useNavigate();

  const { mutateAsync: updatePage, isPending: isUpdatingPage } =
    trpc.nextJSProject.updatePageComponent.useMutation({
      onSuccess: () => {
        void trpcUtils.funnel.list.invalidate({ projectId });
        void trpcUtils.funnel.find.invalidate();
        void trpcUtils.webContainer.getFileTree.invalidate({
          projectId,
        });
      },
    });

  const { mutate: removeFunnelStep, isPending: isRemovingFunnelStep } =
    trpc.funnel.removeFunnelStep.useMutation({
      onSuccess: () => {
        void trpcUtils.funnel.list.invalidate({ projectId });
        void trpcUtils.funnel.find.invalidate();
      },
    });

  const handleSave = async () => {
    if (
      !projectId ||
      (pageData.name === pageName && pageData.pageRoute === pageRoute)
    ) {
      return;
    }

    await updatePage({
      id: pageData.pageId,
      updates: {
        newName: pageName,
        newPageRoute: pageRoute,
      },
      projectId,
    });
    setIsOpen(false);
    setShowPageSettings(false);
  };

  const handlePageSettingsClick = () => {
    setShowPageSettings(true);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setShowPageSettings(false);
    }
  };

  if (!projectId) {
    return null;
  }

  const isLoading = isUpdatingPage || isRemovingFunnelStep;

  const menuContent = (
    <div>
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface rounded-sm"
        onClick={() => {
          navigate(`/shops/${projectId}/site/${pageData.pageId}`);
          setIsOpen(false);
        }}
      >
        <PencilLine size={14} />
        Edit Page
      </div>
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface rounded-sm"
        onClick={handlePageSettingsClick}
      >
        <Settings size={14} />
        Page Settings
      </div>
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface rounded-sm"
        onClick={() => {
          setIsOpen(false);
          removeFunnelStep({
            projectId: projectId,
            stepId: funnelStepId,
          });
        }}
      >
        <Trash2 size={14} />
        Delete Page
      </div>
    </div>
  );

  const settingsContent = (
    <div className="flex flex-col gap-3">
      <div className="text-sm text-medium flex flex-col gap-1">
        <div>Page Name</div>
        <Input
          value={pageName}
          onChange={(e) => setPageName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
        />
      </div>
      <div className="text-sm text-medium flex flex-col gap-1">
        <div>URL Path</div>
        <Input
          value={pageRoute}
          onChange={(e) => setPageRoute(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
        />
      </div>
    </div>
  );

  const settingsFooter = (
    <div className="flex flex-row justify-end gap-2 pt-3 border-t-0.5 border-border">
      <Button variant="secondary" onClick={() => handleOpenChange(false)}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => void handleSave()}
        disabled={isUpdatingPage}
      >
        {isUpdatingPage ? <Loader2 size={12} color="white" /> : "Save"}
      </Button>
    </div>
  );

  return (
    <div className="border-0.5 border-border rounded-[6px] shadow-selectable flex">
      <Popover
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        triggerAsChild
        side="right"
        align="start"
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton={!showPageSettings}
        title={showPageSettings ? "Page Settings" : undefined}
        footer={showPageSettings ? settingsFooter : undefined}
        content={showPageSettings ? settingsContent : menuContent}
        className={twMerge(
          "shadow-menu border-0.5 border-border rounded-md p-3",
          !showPageSettings && "w-[160px] p-2",
        )}
      >
        <IconButton
          icon={<Ellipsis size={14} />}
          variant="tertiary"
          size="sm"
          aria-label="Page actions"
          isLoading={isLoading}
          disabled={isLoading}
        />
      </Popover>
    </div>
  );
};
